import {
  isEventModuleEnabled,
  type ConferenceEvent,
  type EventAppModules,
  type EventModuleKey,
} from '@conference/shared';

export type EventContentTypeKey = Extract<
  EventModuleKey,
  'agenda' | 'speakers' | 'papers' | 'exhibitors' | 'committee'
>;

export interface EventContentType {
  key: EventContentTypeKey;
  label: string;
  description: string;
  icon: 'calendar' | 'user' | 'file' | 'building' | 'users';
  path: (eventId: string) => string;
}

export const EVENT_CONTENT_TYPES: readonly EventContentType[] = [
  {
    key: 'agenda',
    label: 'Sessions',
    description: 'Build the agenda, schedule, tracks, and rooms.',
    icon: 'calendar',
    path: (eventId) => `/events/${eventId}/sessions`,
  },
  {
    key: 'speakers',
    label: 'Speakers',
    description: 'Add presenters and connect them to sessions.',
    icon: 'user',
    path: (eventId) => `/events/${eventId}/speakers`,
  },
  {
    key: 'papers',
    label: 'Papers',
    description: 'Manage research papers and presentation materials.',
    icon: 'file',
    path: (eventId) => `/events/${eventId}/papers`,
  },
  {
    key: 'exhibitors',
    label: 'Organizations',
    description: 'Manage participating organizations and partners.',
    icon: 'building',
    path: (eventId) => `/events/${eventId}/partners`,
  },
  {
    key: 'committee',
    label: 'Committee',
    description: 'Add organizing groups and committee members.',
    icon: 'users',
    path: (eventId) => `/events/${eventId}/committee`,
  },
] as const;

export const EVENT_WORKSPACE_UPDATED_EVENT = 'event-workspace:updated';

export function announceEventWorkspaceUpdated(event: ConferenceEvent): void {
  window.dispatchEvent(new CustomEvent(EVENT_WORKSPACE_UPDATED_EVENT, { detail: event }));
}

export function manualEventModuleDefaults(): EventAppModules {
  return Object.fromEntries(
    EVENT_CONTENT_TYPES.map(({ key }) => [key, { enabled: false }]),
  );
}

export function enabledEventContentTypes(event: Pick<ConferenceEvent, 'app_modules'>): EventContentType[] {
  return EVENT_CONTENT_TYPES.filter(({ key }) => isEventModuleEnabled(event.app_modules, key));
}

export function eventSetupSummary(event: Pick<ConferenceEvent, 'name' | 'date_start' | 'date_end' | 'location' | 'timezone'>) {
  const basicsComplete = [
    event.name,
    event.date_start,
    event.date_end,
    event.location,
    event.timezone,
  ].every((value) => value.trim().length > 0);

  return { basicsComplete };
}
